import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, X } from "lucide-react";
import { CalendlyModal, CALENDLY_URLS } from "@/components/CalendlyModal";

// Neuro-aesthetic color system
const COLORS = {
  warmStone: "#faf7f2",
  oliveSage: "#a07830",
  oliveSageHover: "#8a6628",
  mineralTaupe: "rgba(28,21,16,0.18)",
  warmGold: "#a07830",
  warmGoldHover: "#8a6628",
  inkGrey: "#1c1510",
  // Navy for buttons
  inkNavy: "#1c1510",
  inkNavyHover: "#1c1510",
  softWhite: "#faf7f2",
};

export default function StickyCTA() {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);
  const location = useLocation();
  
  // Determine services link based on funnel
  const isBirthFunnel = location.pathname.startsWith("/birth-readiness");
  const servicesLink = isBirthFunnel ? "/birth-readiness/services" : "/leadership/services";

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const heroHeight = 600;
      
      if (scrollY > heroHeight && !isDismissed) {
        setIsVisible(true);
      } else if (scrollY <= heroHeight) {
        setIsVisible(false);
        setIsDismissed(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isDismissed]);

  if (!isVisible || isDismissed) return null;

  return (
    <>
      <CalendlyModal 
        isOpen={isCalendlyOpen} 
        onClose={() => setIsCalendlyOpen(false)} 
        calendlyUrl={CALENDLY_URLS.clarityCall}
        title="Book a Clarity Call"
      />
      <div 
        className={`fixed bottom-0 left-0 right-0 z-50 transform transition-all duration-500 ease-out ${
          isVisible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
        }`}
        data-testid="sticky-cta"
      >
      {/* Gradient overlay for smooth transition */}
      <div 
        className="absolute inset-0 pointer-events-none" 
        style={{ 
          height: "120%",
          background: `linear-gradient(to top, ${COLORS.warmStone}, ${COLORS.warmStone}, transparent)`
        }} 
      />
      
      {/* CTA Bar */}
      <div 
        className="relative backdrop-blur-md shadow-[0_-4px_20px_rgba(74,71,66,0.06)]"
        style={{ 
          backgroundColor: `${COLORS.warmStone}f0`,
          borderTop: `1px solid ${COLORS.mineralTaupe}50`
        }}
      >
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Text */}
            <div className="hidden sm:block flex-1">
              <p className="font-serif text-sm md:text-base" style={{ color: COLORS.inkGrey }}>
                Ready to experience internal steadiness under pressure?
              </p>
            </div>
            
            {/* Mobile text */}
            <div className="sm:hidden flex-1">
              <p className="font-serif text-sm" style={{ color: COLORS.inkGrey }}>
                Ready to begin?
              </p>
            </div>
            
            {/* CTAs */}
            <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
              <Button 
                variant="outline" 
                onClick={() => setIsCalendlyOpen(true)}
                data-testid="sticky-clarity-call"
                className="rounded-full px-3 md:px-5 py-2 text-[13px] font-medium tracking-wide transition-all duration-300"
                style={{ 
                  borderColor: COLORS.inkNavy, 
                  color: COLORS.inkNavy,
                  backgroundColor: 'transparent'
                }}
              >
                <span className="hidden md:inline">Clarity Call</span>
                <span className="md:hidden">Call</span>
              </Button>
              <Link to={servicesLink} data-testid="sticky-book-session">
                <Button 
                  className="rounded-full px-3 md:px-5 py-2 text-[13px] font-medium tracking-wide transition-all duration-300 flex items-center gap-1 md:gap-2"
                  style={{ 
                    backgroundColor: COLORS.inkNavy,
                    color: COLORS.softWhite
                  }}
                >
                  <span className="hidden md:inline">Book Session</span>
                  <span className="md:hidden">Book</span>
                  <ArrowRight size={14} />
                </Button>
              </Link>
              
              {/* Dismiss button */}
              <button
                onClick={() => setIsDismissed(true)}
                className="p-1.5 transition-colors"
                style={{ color: `${COLORS.inkGrey}80` }}
                aria-label="Dismiss"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
}
